import { Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import type { PopoverPortalTarget } from '@/components/ui/popover-portal';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import {
  GENDER_RULE_ANY,
  genderRuleListboxOptions,
} from '@/lib/gender';

export interface ProfileRuleRow {
  id: string;
  field: string;
  operator: string;
  value: unknown;
}

export interface ProfileCriteriaValue {
  match: 'all' | 'any';
  rules: ProfileRuleRow[];
}

interface RuleFieldMeta {
  field: string;
  kind: string;
  operators: string[];
  enumValues: string[] | null;
}

interface ProgramOption {
  id: string;
  name: string;
}

interface MemberSearchOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface PaginatedResponse<T> {
  data: T[];
  page: { page: number; totalPages: number };
}

interface ProfileCriteriaFieldsProps {
  value: ProfileCriteriaValue;
  onChange: (next: ProfileCriteriaValue) => void;
  popoverPortal?: PopoverPortalTarget;
}

const newRuleId = () => crypto.randomUUID();

const fetchAllPages = async <T,>(path: string): Promise<T[]> => {
  const first = await api.get<PaginatedResponse<T>>(`${path}&page=1`);
  const rows = [...first.data];
  for (let page = 2; page <= first.page.totalPages; page += 1) {
    const next = await api.get<PaginatedResponse<T>>(`${path}&page=${page}`);
    rows.push(...next.data);
  }
  return rows;
};

export const emptyCriteria = (): ProfileCriteriaValue => ({
  match: 'all',
  rules: [
    {
      id: newRuleId(),
      field: 'age',
      operator: 'between',
      value: [18, 35],
    },
  ],
});

export const criteriaPayload = (value: ProfileCriteriaValue) => ({
  match: value.match,
  rules: value.rules.map(({ field, operator, value: ruleValue }) => ({
    field,
    operator,
    value: ruleValue,
  })),
});

export const criteriaFromTemplateParams = (
  params: Record<string, unknown> | null | undefined,
): ProfileCriteriaValue => {
  if (!params || !Array.isArray(params.rules) || params.rules.length === 0) {
    return emptyCriteria();
  }
  return {
    match: params.match === 'any' ? 'any' : 'all',
    rules: params.rules.map((rule) => {
      const row = rule as Record<string, unknown>;
      let ruleValue = row.value ?? null;
      if (row.field === 'gender' && typeof ruleValue === 'string') {
        const allowed = new Set([
          GENDER_RULE_ANY,
          'female',
          'male',
          'other',
          'prefer_not_to_say',
        ]);
        if (!allowed.has(ruleValue)) {
          ruleValue = GENDER_RULE_ANY;
        }
      }
      return {
        id: newRuleId(),
        field: typeof row.field === 'string' ? row.field : 'age',
        operator: typeof row.operator === 'string' ? row.operator : 'between',
        value: ruleValue,
      };
    }),
  };
};

const operatorNeedsValue = (operator: string) => operator !== 'is_set' && operator !== 'is_not_set';

export const ProfileCriteriaFields = ({
  value,
  onChange,
  popoverPortal = 'dialog',
}: ProfileCriteriaFieldsProps) => {
  const { t } = useTranslation('groups');
  const { t: tPeople } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const [memberQuery, setMemberQuery] = useState('');

  const fields = useQuery({
    queryKey: ['groups', 'rule-fields'],
    queryFn: () => api.get<{ data: RuleFieldMeta[] }>('/groups/rule-fields'),
  });

  const needsDepartments = value.rules.some((rule) => rule.field === 'departmentId');
  const needsPrograms = value.rules.some((rule) => rule.field === 'sourceProgramId');
  const needsMembers = value.rules.some((rule) => rule.field === 'sourceInvitedByMemberId');

  const departments = useQuery({
    queryKey: ['people', 'departments', 'options'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> }>('/people/departments'),
    enabled: needsDepartments,
  });

  const programs = useQuery({
    queryKey: ['programs', 'group-rule', 'all'],
    queryFn: () => fetchAllPages<ProgramOption>('/programs?pageSize=100&sort=name&direction=asc'),
    enabled: needsPrograms,
  });

  const memberSearch = useQuery({
    queryKey: ['people', 'group-rule-member-search', memberQuery],
    queryFn: () =>
      api.get<{ data: MemberSearchOption[] }>(
        `/people/members?q=${encodeURIComponent(memberQuery)}&pageSize=20&status=active`,
      ),
    enabled: needsMembers,
  });

  const fieldMap = useMemo(
    () => new Map((fields.data?.data ?? []).map((item) => [item.field, item])),
    [fields.data?.data],
  );

  const fieldOptions = useMemo(
    () =>
      (fields.data?.data ?? []).map((item) => ({
        value: item.field,
        label: t(`criteria.fields.${item.field}`, { defaultValue: item.field }),
      })),
    [fields.data?.data, t],
  );

  const updateRule = (id: string, patch: Partial<ProfileRuleRow>) => {
    onChange({
      ...value,
      rules: value.rules.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)),
    });
  };

  const addRule = () => {
    onChange({
      ...value,
      rules: [
        ...value.rules,
        {
          id: newRuleId(),
          field: 'gender',
          operator: 'equals',
          value: 'female',
        },
      ],
    });
  };

  const removeRule = (id: string) => {
    onChange({
      ...value,
      rules: value.rules.filter((rule) => rule.id !== id),
    });
  };

  const operatorLabel = (operator: string) =>
    t(`criteria.operators.${operator}`, { defaultValue: operator });

  const enumLabel = (field: string, enumValue: string) => {
    if (field === 'gender') {
      if (enumValue === GENDER_RULE_ANY) return t('audience.genderAny');
      return tPeople(`members.gender.${enumValue}`);
    }
    if (field === 'maritalStatus') {
      return tPeople(`members.marital.${enumValue}`);
    }
    if (field === 'memberType') {
      return tPeople(`members.type.${enumValue}`);
    }
    if (field === 'status') {
      return tPeople(`members.status.${enumValue}`);
    }
    if (field === 'receptionMethod') {
      return tPeople(`members.reception.${enumValue}`);
    }
    if (field === 'marriageType') {
      return tPeople(`members.marriageType.${enumValue}`);
    }
    if (field === 'baptismType') {
      return tPeople(`members.baptismType.${enumValue}`);
    }
    return t(`criteria.enumValues.${field}.${enumValue}`, { defaultValue: enumValue });
  };

  const defaultValueForField = (_fieldName: string, meta?: RuleFieldMeta): unknown => {
    if (!meta) return null;
    if (meta.kind === 'boolean') return true;
    if (meta.kind === 'month') return 1;
    if (meta.kind === 'number' && meta.operators.includes('between')) return [18, 35];
    if (meta.kind === 'number') return 18;
    if (meta.enumValues?.length) return meta.enumValues[0];
    if (meta.kind === 'date' && meta.operators.includes('within_last_days')) return 90;
    if (meta.kind === 'date') return new Date().toISOString().slice(0, 10);
    return null;
  };

  const renderValueInput = (rule: ProfileRuleRow) => {
    const meta = fieldMap.get(rule.field);
    if (!meta || !operatorNeedsValue(rule.operator)) {
      return <span className="text-body-sm text-fg-muted">—</span>;
    }

    if (rule.operator === 'between') {
      const pair = Array.isArray(rule.value) ? rule.value : [null, null];
      const isAge = meta.kind === 'number';
      return (
        <div className="grid grid-cols-2 gap-2">
          <Input
            type={isAge ? 'number' : 'text'}
            inputMode={isAge ? 'numeric' : undefined}
            value={pair[0] ?? ''}
            onChange={(event) =>
              updateRule(rule.id, {
                value: [isAge ? Number(event.target.value) : event.target.value, pair[1]],
              })
            }
            placeholder={t('criteria.from')}
          />
          <Input
            type={isAge ? 'number' : 'text'}
            inputMode={isAge ? 'numeric' : undefined}
            value={pair[1] ?? ''}
            onChange={(event) =>
              updateRule(rule.id, {
                value: [pair[0], isAge ? Number(event.target.value) : event.target.value],
              })
            }
            placeholder={t('criteria.to')}
          />
        </div>
      );
    }

    if (meta.kind === 'enum' && meta.enumValues) {
      if (rule.field === 'gender') {
        return (
          <Listbox
            value={typeof rule.value === 'string' ? rule.value : null}
            onChange={(next) => updateRule(rule.id, { value: next })}
            options={genderRuleListboxOptions(t, tPeople)}
            popoverPortal={popoverPortal}
          />
        );
      }

      return (
        <Listbox
          value={typeof rule.value === 'string' ? rule.value : null}
          onChange={(next) => updateRule(rule.id, { value: next })}
          options={meta.enumValues.map((enumValue) => ({
            value: enumValue,
            label: enumLabel(rule.field, enumValue),
          }))}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (meta.kind === 'boolean') {
      return (
        <Listbox
          value={typeof rule.value === 'boolean' ? String(rule.value) : 'true'}
          onChange={(next) => updateRule(rule.id, { value: next === 'true' })}
          options={[
            { value: 'true', label: t('criteria.yes') },
            { value: 'false', label: t('criteria.no') },
          ]}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (meta.kind === 'month') {
      return (
        <Listbox
          value={typeof rule.value === 'number' ? String(rule.value) : '1'}
          onChange={(next) => updateRule(rule.id, { value: Number(next) })}
          options={Array.from({ length: 12 }, (_, index) => ({
            value: String(index + 1),
            label: t(`criteria.months.${index + 1}`),
          }))}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (meta.kind === 'number') {
      return (
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={150}
          value={typeof rule.value === 'number' ? rule.value : ''}
          onChange={(event) => updateRule(rule.id, { value: Number(event.target.value) })}
        />
      );
    }

    if (meta.kind === 'date') {
      if (rule.operator === 'within_last_days' || rule.operator === 'not_within_last_days') {
        return (
          <Input
            type="number"
            inputMode="numeric"
            min={1}
            value={typeof rule.value === 'number' ? rule.value : ''}
            onChange={(event) => updateRule(rule.id, { value: Number(event.target.value) })}
            placeholder={t('criteria.days')}
          />
        );
      }
      return (
        <DatePicker
          value={typeof rule.value === 'string' ? rule.value : null}
          onChange={(next) => updateRule(rule.id, { value: next })}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (rule.field === 'departmentId') {
      return (
        <Listbox
          value={typeof rule.value === 'string' ? rule.value : null}
          onChange={(next) => updateRule(rule.id, { value: next })}
          options={(departments.data?.data ?? []).map((department) => ({
            value: department.id,
            label: department.name,
          }))}
          placeholder={t('criteria.departmentPlaceholder')}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (rule.field === 'sourceProgramId') {
      return (
        <Listbox
          value={typeof rule.value === 'string' ? rule.value : null}
          onChange={(next) => updateRule(rule.id, { value: next })}
          options={(programs.data ?? []).map((program) => ({
            value: program.id,
            label: program.name,
          }))}
          placeholder={t('criteria.programPlaceholder')}
          popoverPortal={popoverPortal}
        />
      );
    }

    if (rule.field === 'sourceInvitedByMemberId') {
      return (
        <EntityCombobox
          value={typeof rule.value === 'string' ? rule.value : null}
          onChange={(next) => updateRule(rule.id, { value: next })}
          options={(memberSearch.data?.data ?? []).map((person) => ({
            id: person.id,
            label: `${person.firstName} ${person.lastName}`.trim(),
          }))}
          onQueryChange={setMemberQuery}
          loading={memberSearch.isFetching}
          placeholder={t('criteria.memberPlaceholder')}
          clearLabel={tCommon('action.clearSelection')}
          popoverPortal={popoverPortal}
        />
      );
    }

    return (
      <Input
        value={typeof rule.value === 'string' ? rule.value : ''}
        onChange={(event) => updateRule(rule.id, { value: event.target.value })}
      />
    );
  };

  const columns: DataTableColumn<ProfileRuleRow>[] = [
    {
      id: 'field',
      header: t('criteria.field'),
      width: '28%',
      cell: (rule) => (
          <Listbox
            value={rule.field}
            onChange={(next) => {
              if (!next) return;
              const nextMeta = fieldMap.get(next);
              const nextOperator = nextMeta?.operators[0] ?? 'equals';
              updateRule(rule.id, {
                field: next,
                operator: nextOperator,
                value: defaultValueForField(next, nextMeta),
              });
            }}
            options={fieldOptions}
            popoverPortal={popoverPortal}
          />
        ),
    },
    {
      id: 'operator',
      header: t('criteria.operator'),
      width: '24%',
      cell: (rule) => {
        const meta = fieldMap.get(rule.field);
        const operators = meta?.operators ?? [];
        return (
          <Listbox
            value={rule.operator}
            onChange={(next) => {
              if (!next) return;
              const nextValue = operatorNeedsValue(next)
                ? defaultValueForField(rule.field, meta)
                : undefined;
              updateRule(rule.id, { operator: next, value: nextValue });
            }}
            options={operators.map((operator) => ({
              value: operator,
              label: operatorLabel(operator),
            }))}
            popoverPortal={popoverPortal}
          />
        );
      },
    },
    {
      id: 'value',
      header: t('criteria.value'),
      cell: (rule) => renderValueInput(rule),
    },
  ];

  if (fields.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-4 w-full max-w-md" />
        <Skeleton className="h-10 w-full max-w-xs" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body-sm text-fg-secondary">{t('criteria.hint')}</p>

      <div className="flex flex-col gap-1.5 sm:max-w-xs">
        <Label>{t('criteria.match')}</Label>
        <Listbox
          value={value.match}
          onChange={(next) => onChange({ ...value, match: next as 'all' | 'any' })}
          options={[
            { value: 'all', label: t('criteria.matchAll') },
            { value: 'any', label: t('criteria.matchAny') },
          ]}
          popoverPortal={popoverPortal}
        />
      </div>

      <DataTable
        rows={value.rules}
        columns={columns}
        rowKey={(rule) => rule.id}
        actionsHeader={t('criteria.actions')}
        rowActions={(rule) =>
          value.rules.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t('criteria.removeRule')}
              onClick={() => removeRule(rule.id)}
            >
              <Trash2 className="size-4" aria-hidden />
            </Button>
          ) : null
        }
        empty={
          <p className="text-body-sm text-fg-secondary">{t('criteria.rulesEmpty')}</p>
        }
      />

      <Button type="button" variant="outline" onClick={addRule} className="self-start">
        <Plus className="size-4" aria-hidden />
        {t('criteria.addRule')}
      </Button>
    </div>
  );
};
